import * as React from "react";
import { Route } from "react-router";
import { EventEmitter } from "fbemitter";

import { LabelStore } from "../flux/Label/LabelStore";
import { FlagStore } from "../flux/Flag/FlagStore";
import { ErrorStore } from "../flux/Error/ErrorStore";
import { ListStore } from "../flux/Grid/ListStore";
import { Vehicle, EntityType, Key } from "../../models";
import { SelectedItemsStore } from "../flux/Grid/SelectedItemsStore";
import { DeleteObjectsStore } from "../flux/DeleteObjects";
import { VehicleBatchEditStore } from "../components/Pages/Vehicle/BatchEdit/flux";
import { ValueStore } from "../flux/Value/ValueStore";

import { SearchWithInitiatorCommand, SearchWithSpinnerCommand } from "../components/Pages/commands/EntitySearchCommand";
import { DeleteObjectsCommand } from "../components/Pages/commands/DeleteObjects";
import { UpsertCommand } from "../components/Pages/commands/UpsertCommand";

import { GoogleMapLoader } from "../utils/GoogleMapLoader";
import { Polyglot } from "../../polyglot";
import { SearchFn } from "../../rest/Search";
import { CommitFn } from "../../rest/Commit";
import { DeleteObjectsFn } from "../../rest/DeleteObjects";
import { GetMeasuresFn } from "../../rest/Misc/measures";
import { GetVehiclesGeopositionFn } from "../../rest/GetVehiclesGeoposition";
import { GetVehicleTagsFn } from "../../rest/Misc";
import { GetGeotabVehiclesFn } from "../../rest/Geotab";
import { GroupActionsFn } from "../../rest/GroupAction";
import { Command1, Command } from "../../Command";
import { VehicleRoute } from "../components/Pages/Vehicle/VehicleRoute";
import { TimezoneStore } from "../utils/TimeZone/TimeZoneStore";
import { TabSearchConfigurator } from "./configureSearchBox";
import { routeFor } from "../routes";
import { SearchProps } from "../components/Pages/SearchProps";
import { EditVehicleStore, UpsertVehicleController } from "../components/Pages/Vehicle/flux";
import { ShowVehicleGeopositionCommand, ShowVehicleGroupActionsCommand, SelectGroupActionCommand, ApplyVehicleGroupActionsCommand } from "../components/Pages/Vehicle/commands";
import { GetGroupParamsFn } from "../../rest/Misc/group-params";

// This class is a configuration for Vehicle tab
// Constructor receives and/or creates dependencies and injects them
export class VehicleTab
{
    readonly markup: JSX.Element;
    readonly searchConfig: TabSearchConfigurator;    
    readonly tableTitleStore = new LabelStore();
    readonly listStore = new ListStore<Vehicle>(v => v.id, EntityType.Vehicle);    
    readonly editStore = new EditVehicleStore();
    readonly searchSensorsCmd: SearchWithInitiatorCommand;
    readonly searchUndefinedLevelsCmd: SearchWithSpinnerCommand;

    private readonly selectedItemsStore = new SelectedItemsStore(this.listStore, EntityType.Vehicle);
    private readonly toDeleteItemsStore = new DeleteObjectsStore(EntityType.Vehicle);
    private readonly toolbarErrorStore = new ErrorStore();
    private readonly mapInProgressStore = new FlagStore();
    private readonly alertStore = new FlagStore();
    private readonly groupActionsErrorStore = new ErrorStore();
    private readonly applyGroupActionsInProgressStore = new FlagStore();
    private readonly groupEditStore = new VehicleBatchEditStore();
    private readonly upsertErrorStore = new ErrorStore();
    private readonly upsertLoadDivisionInProgressStore = new FlagStore();
    private readonly upsertLoadServiceAccountsInProgressStore = new FlagStore();
    private readonly upsertLoadGeotabVehiclesInProgressStore = new FlagStore();
    private readonly upsertLoadTagsInProgressStore = new FlagStore();
    private readonly upsertInProgressStore = new FlagStore();
    private readonly sensorsSearchInitiator = new ValueStore<Key>();
    
    constructor(polyglot: Polyglot, timezoneStore: TimezoneStore, defaultTimezoneId: Key, runSearch: SearchFn, commitEntity: CommitFn, deleteObjects: DeleteObjectsFn, getMeasures: GetMeasuresFn, getGroupParams: GetGroupParamsFn, getVehiclesGeoposition: GetVehiclesGeopositionFn, getVehicleTags: GetVehicleTagsFn, getGeotabVehicles: GetGeotabVehiclesFn, groupActions: GroupActionsFn, getCurrentDivisionId: () => number, divisionEmitter: EventEmitter, googleMapLoader: GoogleMapLoader)
    {
        this.searchConfig = new TabSearchConfigurator(polyglot, EntityType.Vehicle, getCurrentDivisionId, divisionEmitter, this.tableTitleStore.dispatchToken);
        this.searchUndefinedLevelsCmd = new SearchWithSpinnerCommand(polyglot, EntityType.Vehicle, runSearch, this.searchConfig.searchBoxProps.refresher, this.searchConfig.searchInProgressStore.dispatchToken, this.searchConfig.searchErrorStore.dispatchToken);
        this.searchUndefinedLevelsCmd.labelDispatchToken = this.tableTitleStore.dispatchToken;
        const deleteVehicleCommand = new DeleteObjectsCommand(EntityType.Vehicle, this.toDeleteItemsStore, deleteObjects);
        const showVehicleGeopositionCmd = new ShowVehicleGeopositionCommand(() => this.selectedItemsStore.toArray(), getVehiclesGeoposition, googleMapLoader, this.toolbarErrorStore.dispatchToken, this.mapInProgressStore.dispatchToken, this.alertStore.dispatchToken);
        const showVehicleGroupActionsCommand = new ShowVehicleGroupActionsCommand(this.listStore, this.selectedItemsStore, this.groupActionsErrorStore.dispatchToken);
        const selectGroupActionCommand = new SelectGroupActionCommand(this.groupEditStore.getState, getMeasures, getGroupParams, getVehicleTags);
        const applyGroupActionsCommand = new ApplyVehicleGroupActionsCommand(this.groupEditStore.getState, groupActions, this.groupActionsErrorStore.dispatchToken, this.applyGroupActionsInProgressStore.dispatchToken);
        const vehicleUpsertCmd = new UpsertCommand(commitEntity, this.listStore.dispatchToken, this.upsertInProgressStore.dispatchToken, this.upsertErrorStore.dispatchToken);
        const upsertVehicleController = new UpsertVehicleController(polyglot, runSearch, getGeotabVehicles, getVehicleTags, commitEntity, getCurrentDivisionId,
            this.editStore,
            vehicleUpsertCmd,
            this.upsertErrorStore.dispatchToken,
            this.upsertLoadDivisionInProgressStore.dispatchToken,
            this.upsertLoadServiceAccountsInProgressStore.dispatchToken,
            this.upsertLoadGeotabVehiclesInProgressStore.dispatchToken,
            this.upsertLoadTagsInProgressStore.dispatchToken);
        upsertVehicleController.defaultTimezoneId = defaultTimezoneId;
        this.searchSensorsCmd = new SearchWithInitiatorCommand(polyglot, EntityType.Sensor, runSearch, this.sensorsSearchInitiator.dispatchToken, this.toolbarErrorStore.dispatchToken);

        this.markup = (
            <Route path={routeFor(EntityType.Vehicle)} render={() => (
                <VehicleRoute
                    polyglot={polyglot}
                    isAdmin={true}
                    toolbarErrorStore={this.toolbarErrorStore}
                    tableTitleStore={this.tableTitleStore}
                    listStore={this.listStore}
                    selectedItemsStore={this.selectedItemsStore}
                    deleteObjectsStore={this.toDeleteItemsStore}
                    deleteObjectsCommand={deleteVehicleCommand}
                    mapInProgressStore={this.mapInProgressStore}
                    showVehicleGeopositionCommand={showVehicleGeopositionCmd}
                    alertVisibilityStore={this.alertStore}

                    {...this.searchConfig.searchBoxProps}

                    // group actioins
                    selectGroupActionCommand={selectGroupActionCommand}
                    showVehicleGroupActionsCommand={showVehicleGroupActionsCommand}
                    applyGroupActionCommand={applyGroupActionsCommand}
                    applyInProgressStore={this.applyGroupActionsInProgressStore}
                    applyErrorStore={this.groupActionsErrorStore}
                    groupEditStore={this.groupEditStore}
        
                    // dependency injection for upsert form
                    editStore={this.editStore}
                    loadingDivisions={this.upsertLoadDivisionInProgressStore}          
                    loadingServiceAccounts={this.upsertLoadServiceAccountsInProgressStore}
                    loadingGeotabVehicles={this.upsertLoadGeotabVehiclesInProgressStore}
                    loadingTags={this.upsertLoadTagsInProgressStore}
                    upsertInProgress={this.upsertInProgressStore}
                    upsertErrorStore={this.upsertErrorStore}
                    upsertController={upsertVehicleController}
        
                    sensorsSearchCmd={this.searchSensorsCmd}
                    sensorsSearchInitiatorStore={this.sensorsSearchInitiator}
        
                    timezoneStore={timezoneStore}
                />
            )} />
        );
    }
}


// WEBPACK FOOTER //
// ./src/app/init/vehicle.tsx